// Far off, an owned prime plot's building is the same building, baked: built
// from the same spec as the near one (district.ts), then merged into a handful
// of meshes with each part's colour in its vertices (a textured part takes
// its texture's average colour), round things with fewer segments
// (perf/lod.ts) and specks left out. The windows keep the house's own glass,
// and the room light behind them, so they read and light up after dark as the
// near one's do. Every far plot shares these few materials.
import * as THREE from 'three';
import { farGeometry } from './perf/lod.js';
import { glass } from './kit.js';
import { isRoomGlow } from './house.js';

const SOLID = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.86, metalness: 0, envMapIntensity: 0.55 });
const GLOW = new THREE.MeshBasicMaterial({ vertexColors: true });
// the rooms' light after dark, every room's colour in its vertices: on and off with the houses' own
// (house.ts setWindowGlow), copied each frame by farGlow
const ROOM = new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, opacity: 0, depthWrite: false, side: THREE.DoubleSide, visible: false });
let roomLight: THREE.Material | null = null;
export function farGlow() {
  if (!roomLight) return;
  ROOM.visible = roomLight.visible; ROOM.opacity = roomLight.opacity;
}

const avg = new Map<string, THREE.Color>();
function texAverage(tex: THREE.Texture): THREE.Color | null {
  const img: any = tex.image;
  if (!img || !img.width) return null;
  let c = avg.get(tex.uuid);
  if (!c) {
    const cv = document.createElement('canvas'); cv.width = cv.height = 64;
    const g = cv.getContext('2d', { willReadFrequently: true })!;
    g.imageSmoothingEnabled = tex.magFilter !== THREE.NearestFilter;
    try { g.drawImage(img, 0, 0, 64, 64); } catch { return null; }
    const d = g.getImageData(0, 0, 64, 64).data;
    // (averaged as light, not as sRGB numbers: a brick wall's pale mortar counts for what it shows)
    const lin = tex.colorSpace === THREE.SRGBColorSpace ? (x: number) => (x /= 255) < 0.04045 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4 : (x: number) => x / 255;
    let r = 0, gg = 0, b = 0;
    for (let i = 0; i < d.length; i += 4) { r += lin(d[i]); gg += lin(d[i + 1]); b += lin(d[i + 2]); }
    c = new THREE.Color(r / 4096, gg / 4096, b / 4096);
    // (pixel art, drawn sharp, shows its commonest colour from far off, not a blend: a blue flag stays blue)
    if (tex.magFilter === THREE.NearestFilter) {
      const n = new Map<number, number>();
      let best = -1, most = 0;
      for (let i = 0; i < d.length; i += 4) { const k = (d[i] << 16) | (d[i + 1] << 8) | d[i + 2], m = (n.get(k) || 0) + 1; n.set(k, m); if (m > most) { most = m; best = i; } }
      if (best >= 0) c = new THREE.Color(lin(d[best]), lin(d[best + 1]), lin(d[best + 2]));
    }
    avg.set(tex.uuid, c);
  }
  return c;
}

type Bucket = { pos: number[]; nor: number[]; col: number[] };
const bucket = (): Bucket => ({ pos: [], nor: [], col: [] });

// `root` built in its own frame (the plot's 0..w, 0..d); the result is in that frame too. `inside`
// says whether a part is within the walls, unseen from far off (trim round the inside of a window)
export function bakeFar(root: THREE.Object3D, { minSize = 0.12, minTri = 0.004, inside = (_b: THREE.Box3) => false } = {}): THREE.Group {
  const GLASS = glass();
  const solid = bucket(), glow = bucket(), pane = bucket(), room = bucket();
  root.updateMatrixWorld(true);
  const inv = new THREE.Matrix4().copy(root.matrixWorld).invert();
  const m4 = new THREE.Matrix4(), n3 = new THREE.Matrix3(), n = new THREE.Vector3(), scl = new THREE.Vector3();
  const tri = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()], e1 = new THREE.Vector3(), e2 = new THREE.Vector3();
  const im = new THREE.Matrix4(), iw = new THREE.Matrix4(), tint = new THREE.Color(), vc = new THREE.Color(), box = new THREE.Box3();
  root.traverse((o: any) => {
    if (!o.isMesh || o.isSkinnedMesh) return;
    for (let p = o; p; p = p.parent) if (p.visible === false) return;
    if (o.isInstancedMesh) {
      for (let k = 0; k < o.count; k++) {
        o.getMatrixAt(k, im);
        if (o.instanceColor) o.getColorAt(k, tint);
        one(o, iw.multiplyMatrices(o.matrixWorld, im), o.instanceColor ? tint : null);
      }
      return;
    }
    one(o, o.matrixWorld, null);
  });
  function one(o: any, world: THREE.Matrix4, tinted: THREE.Color | null) {
    const geo: THREE.BufferGeometry = o.geometry && farGeometry(o.geometry);
    if (!geo?.attributes?.position) return;
    m4.multiplyMatrices(inv, world);
    if (!geo.boundingSphere) geo.computeBoundingSphere();
    scl.setFromMatrixScale(m4);
    if (geo.boundingSphere!.radius * Math.max(scl.x, scl.y, scl.z) < minSize) return;
    if (!geo.boundingBox) geo.computeBoundingBox();
    const glowy = (Array.isArray(o.material) ? o.material : [o.material]).some((m: any) => isRoomGlow(m));
    if (!glowy && inside(box.copy(geo.boundingBox!).applyMatrix4(m4))) return;
    const ng = geo.index ? geo.toNonIndexed() : geo;
    if (!ng.attributes.normal) ng.computeVertexNormals();
    n3.getNormalMatrix(m4);
    const flip = m4.determinant() < 0;
    const P = ng.attributes.position, N = ng.attributes.normal;
    const mats = Array.isArray(o.material) ? o.material : [o.material];
    const groups = Array.isArray(o.material) && geo.groups.length ? geo.groups : [{ start: 0, count: P.count, materialIndex: 0 }];
    for (const g of groups) {
      const m: any = mats[g.materialIndex ?? 0];
      if (!m) continue;
      let into: Bucket, c: THREE.Color | null = null;
      const em = m.emissive && (m.emissive.r + m.emissive.g + m.emissive.b) > 0.05 && (m.emissiveIntensity ?? 1) > 0.5;
      if (m === GLASS) into = pane;
      else if (isRoomGlow(m)) { into = room; c = m.color; roomLight = m; }
      else if (m.visible === false) continue;
      else if (em) { into = glow; c = m.emissive.clone().multiplyScalar(Math.min(1.6, 0.7 + (m.emissiveIntensity ?? 1) * 0.3)); }
      else if (m.transparent && m.opacity < 0.9) continue;
      else {
        into = solid;
        c = m.color ? m.color.clone() : new THREE.Color(1, 1, 1);
        if (m.map) { const a = texAverage(m.map); if (a) c.multiply(a); }
        if (tinted) c.multiply(tinted);
      }
      const VC = into === solid && m.vertexColors ? ng.attributes.color : null;
      const end = Math.min(P.count, g.start + g.count);
      // a two-sided part (a flag, a sign) is drawn from both sides here too
      for (const back of m.side === THREE.DoubleSide && into === solid ? [false, true] : [false]) {
        for (let t = g.start; t + 2 < end; t += 3) {
          tri[0].fromBufferAttribute(P, t).applyMatrix4(m4); tri[1].fromBufferAttribute(P, t + 1).applyMatrix4(m4); tri[2].fromBufferAttribute(P, t + 2).applyMatrix4(m4);
          // (the end of a thin bar, a speck: a fraction of a pixel from far off)
          if (into === solid && e1.subVectors(tri[1], tri[0]).cross(e2.subVectors(tri[2], tri[0])).length() < minTri * 2) continue;
          for (const j of (back !== flip) ? [0, 2, 1] : [0, 1, 2]) {
            const i = t + j, p = tri[j];
            n.fromBufferAttribute(N, i).applyMatrix3(n3).normalize();
            if (back) n.negate();
            into.pos.push(p.x, p.y, p.z); into.nor.push(n.x, n.y, n.z);
            if (c) {
              if (VC) { vc.fromBufferAttribute(VC as any, i).multiply(c); into.col.push(vc.r, vc.g, vc.b); }
              else into.col.push(c.r, c.g, c.b);
            }
          }
        }
      }
    }
  }
  const out = new THREE.Group();
  const make = (b: Bucket, m: THREE.Material, shadow: boolean) => {
    if (!b.pos.length) return;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(b.pos, 3));
    g.setAttribute('normal', new THREE.BufferAttribute(Int8Array.from(b.nor, (x) => Math.round(Math.max(-1, Math.min(1, x)) * 127)), 3, true));
    // (a plain colour in sixteen bits a channel; a light's may be brighter than white)
    if (b === solid) g.setAttribute('color', new THREE.BufferAttribute(Uint16Array.from(b.col, (x) => Math.round(Math.max(0, Math.min(1, x)) * 65535)), 3, true));
    else if (b.col.length) g.setAttribute('color', new THREE.Float32BufferAttribute(b.col, 3));
    g.computeBoundingSphere();
    const mesh = new THREE.Mesh(g, m);
    mesh.castShadow = shadow; mesh.receiveShadow = shadow;
    out.add(mesh);
  };
  make(solid, SOLID, true);
  make(glow, GLOW, false);
  make(pane, GLASS, false);
  make(room, ROOM, false);
  return out;
}
